/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { Copy, Check, AlertTriangle, AlertOctagon } from 'lucide-react';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

import Modal from '@components/atoms/modals/modal';
import Button from '@components/atoms/ui-kit/button';
import Input from '@components/atoms/ui-kit/input';
import Checkbox from '@components/atoms/ui-kit/checkbox';
import DateInput from '@components/atoms/ui-kit/date-input';
import SelectInput from '@components/atoms/ui-kit/select-input';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { useIssueDirectLicense } from '@utils/api/back-office/licensing';
import { useGetAllLanguages } from '@utils/api/back-office/settings/use-get-all-languages';
import { IssueDirectLicensePayload } from '@utils/types/back-office/licensing';
import { BaseError } from '@utils/types/shared/base';

// Whitelist of realtime-capable BCP47 codes accepted by licensing.scriptix.app
// (#2019). The license service returns opaque 500s for any code outside this
// set, so we filter the picker to match. Update when the licensing service
// adds/removes deployed realtime models.
const REALTIME_CAPABLE_BCP47 = [
  'ar-ar',
  'da-dk',
  'de-de',
  'en-us',
  'es-es',
  'fi-fi',
  'fr-fr',
  'it-it',
  'nb-no',
  'nl-be',
  'nl-nl',
  'nl-pol',
  'sv-se',
  'tl-ph'
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  licensee: string;
  org_limit: number;
  user_limit: number;
  max_processing: number;
  expires: string; // YYYY-MM-DD, empty string = no expiry
  is_online: boolean;
  realtime: boolean;
  max_realtime_streams: number | null;
  realtime_languages: { label: string; value: string }[];
}

/**
 * Issue a new on-prem license directly, without a prior reseller request (#2019).
 *
 * Why this is a separate modal (not an "adapt" of `issue-license-modal.tsx`):
 * the existing modal is tightly coupled to an `AdminLicenseRequest` — its
 * defaults, request-details panel, and copy all assume a request is present.
 * Threading an optional-request mode through it would have made every block
 * conditional and the form harder to read. Two thin modals share only the
 * success view, which is ~50 lines and would gain little from extraction.
 */
export default function IssueDirectLicenseModal({ isOpen, onClose }: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const issueDirect = useIssueDirectLicense();
  const { data: languagesData, isLoading: languagesLoading } =
    useGetAllLanguages();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [licenseKey, setLicenseKey] = useState<string | null>(null);
  const [issuedLicensee, setIssuedLicensee] = useState<string>('');
  const [copied, setCopied] = useState(false);
  // Structured error surfaced inline so the user keeps the context (status,
  // URL, body) while they iterate on the form — toasts time out and a bare
  // "Method Not Allowed" doesn't help diagnose a missing deploy.
  const [submitError, setSubmitError] = useState<{
    status: number | null;
    url: string;
    detail: string;
    hint?: string;
    // Snapshot of the payload we sent, so the operator can share it with the
    // licensing-service owner when chasing an opaque 500.
    payload?: unknown;
  } | null>(null);
  const [payloadCopied, setPayloadCopied] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: {
      licensee: '',
      org_limit: 1,
      user_limit: 10,
      max_processing: 3,
      expires: '',
      is_online: true,
      realtime: false,
      max_realtime_streams: null,
      realtime_languages: []
    }
  });

  const realtimeEnabled = watch('realtime');
  const selectedLangs = watch('realtime_languages');

  // Only show languages the licensing service actually accepts for realtime.
  // The license service (licensing.scriptix.app) has a hardcoded whitelist
  // and returns an opaque 500 when it receives anything else. This list
  // mirrors "Currently-deployed realtime languages" from #2019 — update in
  // lockstep with the licensing service. Match by `bcp47` (the format the
  // license service expects), not `key` (which is often shorter e.g. "sq").
  const languageOptions = useMemo(() => {
    const items = languagesData?.result ?? [];
    return items
      .filter(
        l =>
          l.is_enabled !== false &&
          !l.is_private &&
          REALTIME_CAPABLE_BCP47.includes(l.bcp47)
      )
      .map(l => ({
        label: l.name ? `${l.name} (${l.bcp47})` : l.bcp47,
        value: l.bcp47
      }));
  }, [languagesData]);

  // Bound the expiry picker: no past dates, no more than 5 years out. Prevents
  // sysops from issuing accidentally-past or absurdly-long licenses.
  const { expiresMin, expiresMax } = useMemo(() => {
    const today = new Date();
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    const maxDate = new Date(today);
    maxDate.setFullYear(maxDate.getFullYear() + 5);
    return { expiresMin: iso(today), expiresMax: iso(maxDate) };
  }, []);

  const allSelected =
    languageOptions.length > 0 &&
    selectedLangs.length === languageOptions.length;

  const handleToggleSelectAll = () => {
    setValue('realtime_languages', allSelected ? [] : [...languageOptions], {
      shouldDirty: true
    });
  };

  // Reset state when modal closes so re-opening starts clean
  useEffect(() => {
    if (!isOpen) {
      setLicenseKey(null);
      setIssuedLicensee('');
      setCopied(false);
      setSubmitError(null);
      reset();
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: FormData) => {
    if (!data.licensee.trim()) return;

    // Realtime entitlement without at least one language OR without a stream
    // cap is a shape the license-service rejects (opaque 500). Surface it as
    // a form error instead of round-tripping to a red error banner.
    if (data.realtime && data.realtime_languages.length === 0) {
      setSubmitError({
        status: null,
        url: 'api/admin/licensing/keys',
        detail:
          t('realtime-languages-required') ||
          'Pick at least one realtime language, or turn off the Realtime ASR add-on.'
      });
      return;
    }
    if (
      data.realtime &&
      (data.max_realtime_streams === null ||
        data.max_realtime_streams === undefined ||
        data.max_realtime_streams < 1)
    ) {
      setSubmitError({
        status: null,
        url: 'api/admin/licensing/keys',
        detail:
          t('realtime-streams-required') ||
          'Set the max concurrent realtime streams (≥1), or turn off the Realtime ASR add-on.'
      });
      return;
    }

    // YYYY-MM-DD → ISO datetime at end of day UTC. End-of-day so the user
    // gets the full day they picked; UTC keeps wall-clock equivalence with
    // what the license-service stores.
    const expiresIso = data.expires
      ? new Date(`${data.expires}T23:59:59Z`).toISOString()
      : null;

    // Built outside try/ so the catch block can attach the same payload
    // snapshot to the error banner for copy-and-share diagnosis.
    const payload: IssueDirectLicensePayload = {
      licensee: data.licensee.trim(),
      org_limit: data.org_limit,
      user_limit: data.user_limit,
      max_processing: data.max_processing,
      expires: expiresIso,
      is_online: data.is_online,
      realtime: data.realtime,
      max_realtime_streams: data.realtime
        ? (data.max_realtime_streams ?? null)
        : null,
      realtime_languages: data.realtime
        ? data.realtime_languages.map(o => o.value)
        : null
    };

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const result = await issueDirect.mutateAsync(payload);
      setLicenseKey(result.license_key);
      setIssuedLicensee(data.licensee.trim());
    } catch (err) {
      // Build a structured error from the AxiosError so the modal can render
      // it inline (toast was too terse for "Method Not Allowed" / deploy lag).
      const axiosErr = err as AxiosError<BaseError>;
      const status = axiosErr.response?.status ?? null;
      const body = axiosErr.response?.data;
      const fullUrl = [
        axiosErr.config?.baseURL?.replace(/\/$/, ''),
        axiosErr.config?.url?.replace(/^\//, '')
      ]
        .filter(Boolean)
        .join('/');

      const detail =
        body?.error_description ||
        body?.message ||
        body?.detail ||
        axiosErr.message ||
        t('failed-to-issue-license');

      // Give the operator something actionable per known error class. The
      // licensing_server_error case is our #1 support ticket — a bare 500 from
      // licensing.scriptix.app with no body — so hint at what to try or share.
      const isLicensingUpstream500 =
        body?.error === 'licensing_server_error' ||
        /licensing server returned 5\d\d/i.test(detail);
      const hint =
        status === 405
          ? t('endpoint-not-deployed-hint')
          : status === 401 || status === 403
            ? t('not-authorized-hint')
            : isLicensingUpstream500
              ? t('licensing-service-opaque-500-hint')
              : undefined;

      setSubmitError({
        status,
        url: fullUrl || 'api/admin/licensing/keys',
        detail,
        hint,
        payload
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyKey = async () => {
    if (!licenseKey) return;
    try {
      await navigator.clipboard.writeText(licenseKey);
      setCopied(true);
      toast.success(t('license-key-copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('failed-to-copy'));
    }
  };

  const handleClose = () => {
    onClose();
  };

  // ─── Success view ──────────────────────────────────────────────────
  if (licenseKey) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size="medium"
        title={t('license-issued-successfully')}
        closeOnOutsideClick={false}
        closeOnEsc={false}
        footerActions={[
          <Button
            key="done"
            onClick={handleClose}
            label={t('done')}
            size="small"
            variant="primary"
          />
        ]}
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-400/10 dark:bg-amber-400/15">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              {t('license-key-warning')}
            </p>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: getColour('normalText') }}
            >
              {t('license-key')}
            </label>
            <div className="relative">
              <textarea
                readOnly
                value={licenseKey}
                rows={4}
                className="w-full p-3 pr-12 rounded-2xl border border-hairline bg-app text-xs font-mono resize-none"
                style={{ color: getColour('normalText') }}
              />
              <button
                type="button"
                onClick={handleCopyKey}
                className="absolute top-3 right-3 p-2 rounded-full border border-hairline bg-app-soft hover:opacity-80 transition-opacity"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy
                    className="h-4 w-4"
                    style={{ color: getColour('normalText') }}
                  />
                )}
              </button>
            </div>
          </div>

          <div
            className="p-3 rounded-2xl border border-hairline bg-app text-sm"
            style={{ color: getColour('inactiveText') }}
          >
            <p>
              <span style={{ color: getColour('normalText') }}>
                {t('licensee')}:
              </span>{' '}
              {issuedLicensee}
            </p>
          </div>
        </div>
      </Modal>
    );
  }

  // ─── Form view ─────────────────────────────────────────────────────
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="medium"
      title={t('issue-new-license')}
      caption={t('issue-new-license-caption')}
      footerActions={[
        <Button
          key="cancel"
          onClick={handleClose}
          label={t('cancel')}
          size="small"
          variant="smoke"
          disabled={isSubmitting}
        />,
        <Button
          key="issue"
          onClick={handleSubmit(onSubmit)}
          label={isSubmitting ? t('issuing') : t('issue-license')}
          size="small"
          variant="primary"
          disabled={isSubmitting}
        />
      ]}
    >
      <form className="space-y-4">
        {/* Structured error block — kept inline so it doesn't disappear like
            a toast and shows enough detail to debug deploy/route issues. */}
        {submitError && (
          <div
            className="flex items-start gap-3 p-3 rounded-2xl bg-red-500/10 border border-red-500/25 dark:bg-red-500/15 dark:border-red-500/30"
            role="alert"
          >
            <AlertOctagon className="h-5 w-5 flex-shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
            <div className="min-w-0 flex-1 text-sm">
              <div className="font-semibold text-red-800 dark:text-red-300">
                {submitError.status
                  ? `${submitError.status} — ${t('failed-to-issue-license')}`
                  : t('failed-to-issue-license')}
              </div>
              <div className="mt-0.5 break-words text-red-800 dark:text-red-300">
                {submitError.detail}
              </div>
              {submitError.hint && (
                <div className="mt-1.5 text-xs text-red-700 dark:text-red-400">
                  {submitError.hint}
                </div>
              )}
              <div className="mt-1.5 text-xs font-mono break-all opacity-80 text-red-700 dark:text-red-400">
                POST {submitError.url}
              </div>
              {submitError.payload !== undefined && (
                <details className="mt-2">
                  <summary className="text-xs cursor-pointer underline text-red-700 dark:text-red-400">
                    {t('show-request-payload') || 'Show request payload'}
                  </summary>
                  <div className="mt-1.5 flex items-start gap-2">
                    <pre className="flex-1 text-[11px] font-mono whitespace-pre-wrap break-all p-2 rounded-lg overflow-auto max-h-40 bg-red-900/5 dark:bg-red-900/15 text-red-700 dark:text-red-400">
                      {JSON.stringify(submitError.payload, null, 2)}
                    </pre>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(
                            JSON.stringify(submitError.payload, null, 2)
                          );
                          setPayloadCopied(true);
                          setTimeout(() => setPayloadCopied(false), 2000);
                        } catch {
                          /* clipboard blocked */
                        }
                      }}
                      className="flex-shrink-0 p-1.5 rounded-md hover:bg-white/10 transition-colors"
                      title={t('copy-payload') || 'Copy payload'}
                      aria-label={t('copy-payload') || 'Copy payload'}
                    >
                      {payloadCopied ? (
                        <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                      ) : (
                        <Copy className="h-3.5 w-3.5 text-red-700 dark:text-red-400" />
                      )}
                    </button>
                  </div>
                </details>
              )}
            </div>
          </div>
        )}

        {/* Licensee — licensing-service returns opaque 500s for very short
            names (observed: "fdb" → 500). Enforce ≥4 chars client-side so the
            operator gets a clear message instead of the wrapped server 500. */}
        <Input
          id="licensee"
          label={t('licensee')}
          size="small"
          required
          placeholder={t('licensee-placeholder')}
          error={errors.licensee?.message}
          {...register('licensee', {
            required: t('licensee-required') || 'Licensee is required',
            minLength: {
              value: 4,
              message:
                t('licensee-min-length') ||
                'Licensee must be at least 4 characters'
            }
          })}
        />

        {/* Limits */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            id="org_limit"
            label={t('org-limit')}
            type="number"
            min={1}
            size="small"
            {...register('org_limit', { valueAsNumber: true, min: 1 })}
          />
          <Input
            id="user_limit"
            label={t('user-limit')}
            type="number"
            min={1}
            size="small"
            {...register('user_limit', { valueAsNumber: true, min: 1 })}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            id="max_processing"
            label={t('max-processing')}
            type="number"
            min={1}
            size="small"
            {...register('max_processing', { valueAsNumber: true, min: 1 })}
          />
          {/* Expires */}
          <Controller
            name="expires"
            control={control}
            // min/max bound the picker and drive DateInput's inline error, but
            // neither blocks submission — the field is a native date input, so
            // an out-of-range value can still be typed rather than picked.
            // This is the submit-time guard. Empty means "no expiry".
            rules={{
              validate: v =>
                !v ||
                (v >= expiresMin && v <= expiresMax) ||
                t('expiry-out-of-range')
            }}
            render={({ field }) => (
              <DateInput
                id="expires"
                label={t('expires')}
                size="small"
                value={field.value || ''}
                onChange={v => field.onChange(v)}
                placeholder={t('no-expiry-placeholder') || 'No expiry'}
                min={expiresMin}
                max={expiresMax}
              />
            )}
          />
        </div>

        {/* Online mode */}
        <div className="border-t border-hairline pt-4">
          <Checkbox
            id="is_online"
            label={t('online-mode')}
            {...register('is_online')}
            variant="primary"
            wrapperClass="flex items-center gap-2"
            labelClass="text-sm font-medium"
          />
          <p
            className="text-xs mt-1 ml-6"
            style={{ color: getColour('inactiveText') }}
          >
            {t('online-validation-description')}
          </p>
        </div>

        {/* Realtime */}
        <div className="border-t border-hairline pt-4 space-y-3">
          <Checkbox
            id="realtime"
            label={t('realtime-asr-add-on')}
            {...register('realtime')}
            variant="primary"
            wrapperClass="flex items-center gap-2"
            labelClass="text-sm font-medium"
          />
          <p
            className="text-xs ml-6 -mt-2"
            style={{ color: getColour('inactiveText') }}
          >
            {t('realtime-asr-help')}
          </p>

          {realtimeEnabled ? (
            <div className="space-y-3">
              <Input
                id="max_realtime_streams"
                label={t('max-realtime-streams')}
                type="number"
                min={1}
                size="small"
                wrapperClass="w-full sm:w-1/2"
                {...register('max_realtime_streams', {
                  setValueAs: v =>
                    v === '' || v === null || v === undefined ? null : Number(v)
                })}
                placeholder={t('max-realtime-streams-placeholder') || 'e.g. 4'}
              />
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label
                    className="block text-sm font-semibold pl-1"
                    style={{ color: getColour('normalText') }}
                  >
                    {t('realtime-languages')}
                  </label>
                  <button
                    type="button"
                    onClick={handleToggleSelectAll}
                    disabled={languagesLoading || languageOptions.length === 0}
                    className="text-xs underline hover:no-underline disabled:opacity-50"
                    style={{ color: getColour('activeText') }}
                  >
                    {allSelected ? t('clear-all') : t('select-all')}
                  </button>
                </div>
                <Controller
                  name="realtime_languages"
                  control={control}
                  render={({ field }) => (
                    <SelectInput
                      isMulti
                      isSearchable
                      placeholder={t('select-realtime-languages')}
                      options={languageOptions}
                      value={field.value}
                      onChange={selected =>
                        field.onChange(Array.isArray(selected) ? selected : [])
                      }
                    />
                  )}
                />
              </div>
            </div>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
